import {
  arrayOf,
  boolOf,
  fail,
  idOf,
  numberOf,
  objectOf,
  optionalBool,
  optionalNumber,
  optionalText,
  textOf,
} from '../../../shared/api/wire'
import type {
  AqsiAttentionSale,
  AqsiSaleAttention,
  AttentionLevel,
  AqsiHeldCart,
  AqsiHeldCartLine,
  AqsiHeldCarts,
  AqsiSale,
  AqsiSaleCheck,
  AqsiSaleCurrentOperation,
  AqsiSaleHoldResult,
  AqsiSaleFiscalSnapshot,
  AqsiSaleLine,
  AqsiSaleOperation,
  AqsiSeatActivity,
  FiscalState,
  ManualResolution,
  OperationNextAction,
  OperationState,
  PaymentKind,
  PaymentState,
  SaleOperationKind,
} from '../model/types'

const PAYMENT_KINDS: readonly PaymentKind[] = ['Cash', 'Card']
const PAYMENT_STATES: readonly PaymentState[] = ['NotRequired', 'Pending', 'InProgress', 'Succeeded', 'Failed', 'Unknown']
const FISCAL_STATES: readonly FiscalState[] = ['Pending', 'InProgress', 'Succeeded', 'Failed', 'Unknown', 'IssuedOutside']
const OPERATION_STATES: readonly OperationState[] = ['Pending', 'Sent', 'Succeeded', 'Failed', 'Unknown']
const NEXT_ACTIONS: readonly OperationNextAction[] = ['None', 'Reconcile', 'ManualReconciliation']
const OPERATION_KINDS: Readonly<Record<string, SaleOperationKind>> = {
  'aqsi.sale.purchase': 'purchase',
  'aqsi.sale.receipt': 'receipt',
  // C4b: деньги и чек возврата — отдельные операции того же журнала; ручная сверка у них та же (UC1d).
  'aqsi.refund.payment': 'refundPayment',
  'aqsi.refund.receipt': 'refundReceipt',
}

function oneOf<T extends string>(value: unknown, allowed: readonly T[], field: string): T {
  const text = textOf(value, field)
  return (allowed as readonly string[]).includes(text) ? (text as T) : fail(`${field}: неизвестное значение ${text}`)
}

/** Копейки — целое: дробные «копейки» значат ответ другой схемы. */
function kopecksOf(value: unknown, field: string): number {
  const number = numberOf(value, field)
  return Number.isSafeInteger(number) ? number : fail(`${field} — не целые копейки`)
}

/**
 * Количество — `numeric(18,3)` службы; в JSON — число. Клиент держит его
 * строкой для показа и не считает с ним.
 */
function quantityOf(value: unknown, field: string): string {
  if (typeof value === 'string' && /^\d+(\.\d{1,3})?$/.test(value)) return value
  const number = numberOf(value, field)
  const text = String(number)
  return number > 0 && /^\d+(\.\d{1,3})?$/.test(text) ? text : fail(`${field} — не количество`)
}

function decodeLine(raw: unknown, field: string): AqsiSaleLine {
  const item = objectOf(raw, field)
  return {
    id: idOf(item.id, `${field}.id`),
    ordinal: numberOf(item.ordinal, `${field}.ordinal`),
    name: textOf(item.name, `${field}.name`),
    barcode: optionalText(item.barcode, `${field}.barcode`),
    quantity: quantityOf(item.quantity, `${field}.quantity`),
    priceKopecks: kopecksOf(item.priceKopecks, `${field}.priceKopecks`),
    amountKopecks: kopecksOf(item.amountKopecks, `${field}.amountKopecks`),
    // C3b всегда пишет ключ товара кабинета: без него позицию не отправить обратно PUT-ом.
    productKey: idOf(item.productResourceKey, `${field}.productResourceKey`),
    // UI-RMK (Resource.Aqsi 0.22.0): единица и весовой товар — необязательные поля, прежняя служба их не отдаёт.
    unit: optionalText(item.unit, `${field}.unit`),
    isWeight: optionalBool(item.isWeight, `${field}.isWeight`),
    // RP-FB8: цена кассира — исходная цена кабинета и кто поменял; прежняя служба полей не отдаёт.
    catalogPriceKopecks:
      item.catalogPriceKopecks == null ? null : kopecksOf(item.catalogPriceKopecks, `${field}.catalogPriceKopecks`),
    priceChangedBy: optionalText(item.priceChangedBy, `${field}.priceChangedBy`),
  }
}

const ATTENTION_LEVELS: readonly AttentionLevel[] = ['critical', 'warning']
/** Код, при котором служба требует действий сейчас; остальные — внимание. */
const CRITICAL_ATTENTION: ReadonlySet<string> = new Set(['duplicate-suspected'])

/**
 * Предупреждения службы по продаже (`attention`): объект или список `{ code, level?, text? }`. Уровень, если службы не назвала, —
 * по коду. Тяжёлые первыми. Не пришло — пусто (прежняя служба).
 */
export function decodeAttention(raw: unknown, field: string): readonly AqsiSaleAttention[] {
  if (raw === null || raw === undefined) return []
  const items = Array.isArray(raw) ? raw : [raw]
  const result = items.map((item, index): AqsiSaleAttention => {
    const data = objectOf(item, `${field}[${index}]`)
    const code = textOf(data.code, `${field}[${index}].code`)
    const level = typeof data.level === 'string' ? data.level.toLowerCase() : null
    return {
      code,
      level: (ATTENTION_LEVELS as readonly string[]).includes(level ?? '')
        ? (level as AttentionLevel)
        : CRITICAL_ATTENTION.has(code)
          ? 'critical'
          : 'warning',
      text: optionalText(data.text, `${field}[${index}].text`),
      since: optionalText(data.since, `${field}[${index}].since`),
      nextAttemptAt: optionalText(data.nextAttemptAt, `${field}[${index}].nextAttemptAt`),
    }
  })
  return result.sort((left, right) => Number(right.level === 'critical') - Number(left.level === 'critical'))
}

/** Продажа `aqsi.sale` (схема `sale` draft.2 + отклонения C3b: наш UUID операции, версия). */
export function decodeSale(raw: unknown, field = 'продажа'): AqsiSale {
  const data = objectOf(raw, field)
  // Возврат (C4b) приходит тем же видом конверта: окно продажи его не показывает как продажу.
  if (data.kind !== undefined && data.kind !== 'Sale') fail(`${field}.kind: ожидалась продажа`)
  return {
    id: idOf(data.id, `${field}.id`),
    createdAt: textOf(data.createdAt, `${field}.createdAt`),
    updatedAt: textOf(data.updatedAt, `${field}.updatedAt`),
    paymentKind: oneOf(data.paymentKind, PAYMENT_KINDS, `${field}.paymentKind`),
    totalKopecks: kopecksOf(data.totalKopecks, `${field}.totalKopecks`),
    payment: oneOf(data.payment, PAYMENT_STATES, `${field}.payment`),
    fiscal: oneOf(data.fiscal, FISCAL_STATES, `${field}.fiscal`),
    purchaseOperationId: optionalText(data.purchaseOperationId, `${field}.purchaseOperationId`),
    slipId: optionalText(data.slipId, `${field}.slipId`),
    receiptOperationId: optionalText(data.receiptOperationId, `${field}.receiptOperationId`),
    receiptId: optionalText(data.receiptId, `${field}.receiptId`),
    lastMessage: optionalText(data.lastMessage, `${field}.lastMessage`),
    isFinished: boolOf(data.isFinished, `${field}.isFinished`),
    lines: arrayOf(data.lines, `${field}.lines`).map((item, index) => decodeLine(item, `${field}.lines[${index}]`)),
    version: numberOf(data.version, `${field}.version`),
    customerContact: optionalText(data.customerContact, `${field}.customerContact`),
    attention: decodeAttention(data.attention, `${field}.attention`),
    receiptRetryAllowedAt: optionalText(data.receiptRetryAllowedAt, `${field}.receiptRetryAllowedAt`),
    receiptCanRetryNow: decodeCanRetryNow(data.receiptAttempt, `${field}.receiptAttempt`),
    isParked: optionalBool(data.isParked, `${field}.isParked`) ?? false,
    currentOperation: decodeCurrentOperation(data.currentOperation, `${field}.currentOperation`),
  }
}

/**
 * Текущая операция продажи (`currentOperation`, служба 0.89.0, RP-PAY-ONE шаг 6): те же слова, что у `decodeSaleOperation`;
 * `null` или поля нет (служба старее) — `null`.
 */
export function decodeCurrentOperation(raw: unknown, field: string): AqsiSaleCurrentOperation | null {
  if (raw === null || raw === undefined) return null
  const data = objectOf(raw, field)
  const kindText = textOf(data.kind, `${field}.kind`)
  return {
    operationId: idOf(data.operationId, `${field}.operationId`),
    kind: OPERATION_KINDS[kindText] ?? fail(`${field}.kind ${kindText} — не операция продажи`),
    state: oneOf(data.state, OPERATION_STATES, `${field}.state`),
    providerStatus: optionalText(data.providerStatus, `${field}.providerStatus`),
    providerMessage: optionalText(data.providerMessage, `${field}.providerMessage`),
    nextAction: oneOf(data.nextAction, NEXT_ACTIONS, `${field}.nextAction`),
    manualRequired: boolOf(data.manualRequired, `${field}.manualRequired`),
    accepted: boolOf(data.accepted, `${field}.accepted`),
    sentAt: optionalText(data.sentAt, `${field}.sentAt`),
    deadline: optionalText(data.deadline, `${field}.deadline`),
  }
}

/**
 * `GET sales/attention`: массив строк `{ saleId, deviceId, createdAt, updatedAt, totalKopecks, payment, fiscal, isFinished, attention }`,
 * голый или в конверте (`data`). Свежие первыми — порядок службы сохраняется.
 */
export function decodeAttentionSales(raw: unknown): readonly AqsiAttentionSale[] {
  const list = Array.isArray(raw) ? raw : arrayOf(objectOf(raw, 'требуют внимания').data, 'data')
  return list.map((entry, index): AqsiAttentionSale => {
    const field = `требуют внимания[${index}]`
    const data = objectOf(entry, field)
    return {
      saleId: idOf(data.saleId, `${field}.saleId`),
      deviceId: numberOf(data.deviceId, `${field}.deviceId`),
      createdAt: textOf(data.createdAt, `${field}.createdAt`),
      updatedAt: textOf(data.updatedAt, `${field}.updatedAt`),
      totalKopecks: kopecksOf(data.totalKopecks, `${field}.totalKopecks`),
      payment: oneOf(data.payment, PAYMENT_STATES, `${field}.payment`),
      fiscal: oneOf(data.fiscal, FISCAL_STATES, `${field}.fiscal`),
      isFinished: boolOf(data.isFinished, `${field}.isFinished`),
      attention: decodeAttention(data.attention, `${field}.attention`),
    }
  })
}

/** `GET sales/parked`: отложенные продажи ресурса (`AqsiSaleDto`), свежие первыми. */
export function decodeParkedSales(raw: unknown): readonly AqsiSale[] {
  return arrayOf(raw, 'отложенные продажи').map((item, index) => decodeSale(item, `отложенные продажи[${index}]`))
}

/** `GET sales/parked/count`: число отложенных продаж с деньгами. */
export function decodeParkedCount(raw: unknown): number {
  const count = numberOf(objectOf(raw, 'число отложенных').count, 'count')
  return Number.isSafeInteger(count) && count >= 0 ? count : fail('count — не число продаж')
}

function decodeHeldLine(raw: unknown, field: string): AqsiHeldCartLine {
  const item = objectOf(raw, field)
  return {
    productKey: idOf(item.productResourceKey, `${field}.productResourceKey`),
    name: textOf(item.name, `${field}.name`),
    barcode: optionalText(item.barcode, `${field}.barcode`),
    quantity: quantityOf(item.quantity, `${field}.quantity`),
    priceKopecks: kopecksOf(item.priceKopecks, `${field}.priceKopecks`),
    amountKopecks: kopecksOf(item.amountKopecks, `${field}.amountKopecks`),
  }
}

/** Отложенный чек `aqsi.held-cart` (UI-RMK). */
export function decodeHeldCart(raw: unknown, field = 'отложенный чек'): AqsiHeldCart {
  const data = objectOf(raw, field)
  return {
    id: idOf(data.id, `${field}.id`),
    registerKey: idOf(data.registerKey, `${field}.registerKey`),
    heldAt: textOf(data.heldAt, `${field}.heldAt`),
    heldByName: textOf(data.heldByName, `${field}.heldByName`),
    paymentKind: oneOf(data.paymentKind, PAYMENT_KINDS, `${field}.paymentKind`),
    customerContact: optionalText(data.customerContact, `${field}.customerContact`),
    totalKopecks: kopecksOf(data.totalKopecks, `${field}.totalKopecks`),
    lines: arrayOf(data.lines, `${field}.lines`).map((item, index) => decodeHeldLine(item, `${field}.lines[${index}]`)),
    version: numberOf(data.version, `${field}.version`),
  }
}

/** Отложенные чеки кассы места `aqsi.held-carts`. */
export function decodeHeldCarts(raw: unknown): AqsiHeldCarts {
  const data = objectOf(raw, 'отложенные чеки')
  return {
    registerKey: idOf(data.registerKey, 'registerKey'),
    carts: arrayOf(data.carts, 'carts').map((item, index) => decodeHeldCart(item, `carts[${index}]`)),
  }
}

/** Итог «Отложить» `aqsi.sale.hold`: отменённая продажа и отложенный чек. */
export function decodeHoldResult(raw: unknown): AqsiSaleHoldResult {
  const data = objectOf(raw, 'отложить')
  return { sale: decodeSale(data.sale, 'sale'), held: decodeHeldCart(data.held, 'held') }
}

/**
 * `GET sales/active`: `null` — на кассе места ничего незавершённого (только
 * тогда); продажа — целиком; возврат (C4b, тот же конверт `aqsi.sale` с
 * `kind=Refund`) — только его id и исходный чек: окно продажи возврат не ведёт.
 */
export function decodeActiveSale(raw: unknown): AqsiSeatActivity {
  if (raw === null || raw === undefined) return null
  const data = objectOf(raw, 'продажа кассы')
  if (data.kind === 'Refund') {
    return {
      kind: 'refund',
      refundId: idOf(data.id, 'возврат кассы.id'),
      receiptId: idOf(data.sourceReceiptId, 'возврат кассы.sourceReceiptId'),
    }
  }
  // Возврат по слипу без чека (RP-FB74) тоже держит кассу, но исходного чека у него нет: пустой номер чека - его признак.
  if (data.kind === 'SlipRefund') return { kind: 'refund', refundId: idOf(data.id, 'возврат по слипу.id'), receiptId: '' }
  return decodeSale(raw)
}

function decodeResolution(raw: unknown): ManualResolution | null {
  if (raw === null || raw === undefined) return null
  const item = objectOf(raw, 'resolution')
  return {
    resolvedBy: textOf(item.resolvedBy, 'resolution.resolvedBy'),
    outcome: textOf(item.outcome, 'resolution.outcome'),
    reason: textOf(item.reason, 'resolution.reason'),
    checked: textOf(item.checked, 'resolution.checked'),
    at: textOf(item.at, 'resolution.at'),
  }
}

/**
 * Статус операции продажи. Тот же маршрут отдаёт и команды смены C3a — их вид
 * здесь отвергается: окно продажи не должно молча показать смену как оплату.
 */
export function decodeSaleOperation(raw: unknown): AqsiSaleOperation {
  const data = objectOf(raw, 'операция')
  const kindText = textOf(data.kind, 'kind')
  const kind = OPERATION_KINDS[kindText] ?? fail(`kind ${kindText} — не операция продажи`)
  return {
    operationId: idOf(data.operationId, 'operationId'),
    kind,
    saleId: idOf(data.saleId, 'saleId'),
    registerKey: idOf(data.registerKey, 'registerKey'),
    amountKopecks: kopecksOf(data.amountKopecks, 'amountKopecks'),
    state: oneOf(data.state, OPERATION_STATES, 'state'),
    attemptState: textOf(data.attemptState, 'attemptState'),
    externalOperationId: optionalText(data.externalOperationId, 'externalOperationId'),
    resultId: optionalText(data.resultId, 'resultId'),
    providerStatus: optionalText(data.providerStatus, 'providerStatus'),
    providerMessage: optionalText(data.providerMessage, 'providerMessage'),
    note: optionalText(data.note, 'note'),
    createdAt: textOf(data.createdAt, 'createdAt'),
    sentAt: optionalText(data.sentAt, 'sentAt'),
    checkedAt: optionalText(data.checkedAt, 'checkedAt'),
    resolvedAt: optionalText(data.resolvedAt, 'resolvedAt'),
    nextCheckAt: optionalText(data.nextCheckAt, 'nextCheckAt'),
    nextAction: oneOf(data.nextAction, NEXT_ACTIONS, 'nextAction'),
    version: numberOf(data.version, 'version'),
    resolution: decodeResolution(data.resolution),
  }
}

/** Снимок реквизитов `aqsi.sale.fiscal-profile`: только то, что показывает экран. */
export function decodeFiscalSnapshot(raw: unknown): AqsiSaleFiscalSnapshot {
  const data = objectOf(raw, 'снимок реквизитов')
  const profile = objectOf(data.profile, 'profile')
  const device = objectOf(profile.device, 'profile.device')
  const settings = objectOf(data.settings, 'settings')
  return {
    saleId: idOf(data.saleId, 'saleId'),
    vatSource: textOf(data.vatSource, 'vatSource'),
    capturedAt: textOf(data.capturedAt, 'capturedAt'),
    taxSystem: numberOf(device.taxSystem, 'profile.device.taxSystem'),
    taxSystemSource: textOf(device.taxSystemSource, 'profile.device.taxSystemSource'),
    deviceSerial: textOf(device.serialNumber, 'profile.device.serialNumber'),
    cashierName: textOf(settings.cashierName, 'settings.cashierName'),
    paymentMode: textOf(settings.paymentMode, 'settings.paymentMode'),
    lines: arrayOf(profile.lines, 'profile.lines').map((item, index) => {
      const line = objectOf(item, `profile.lines[${index}]`)
      return {
        goodId: textOf(line.goodId, `profile.lines[${index}].goodId`),
        name: textOf(line.name, `profile.lines[${index}].name`),
        tax: numberOf(line.tax, `profile.lines[${index}].tax`),
        unit: optionalText(line.unit, `profile.lines[${index}].unit`),
        source: textOf(line.source, `profile.lines[${index}].source`),
        observedAt: textOf(line.observedAt, `profile.lines[${index}].observedAt`),
      }
    }),
  }
}

/** Проверка корзины `aqsi.sale.check` (С2). */
export function decodeSaleCheck(raw: unknown): AqsiSaleCheck {
  const data = objectOf(raw, 'проверка корзины')
  return {
    saleId: idOf(data.saleId, 'saleId'),
    saleVersion: numberOf(data.saleVersion, 'saleVersion'),
    deviceId: numberOf(data.deviceId, 'deviceId'),
    canSell: boolOf(data.canSell, 'canSell'),
    summary: textOf(data.summary, 'summary'),
    code: optionalText(data.code, 'code'),
    reasons: arrayOf(data.reasons ?? [], 'reasons').map((item, index) => {
      const reason = objectOf(item, `reasons[${index}]`)
      return {
        code: textOf(reason.code, `reasons[${index}].code`),
        scope: textOf(reason.scope, `reasons[${index}].scope`),
        message: textOf(reason.message, `reasons[${index}].message`),
        fixBy: textOf(reason.fixBy, `reasons[${index}].fixBy`),
        action: textOf(reason.action, `reasons[${index}].action`),
        detail: optionalText(reason.detail, `reasons[${index}].detail`),
        lineId: optionalText(reason.lineId, `reasons[${index}].lineId`),
        ordinal: optionalNumber(reason.ordinal, `reasons[${index}].ordinal`),
        goodId: optionalText(reason.goodId, `reasons[${index}].goodId`),
      }
    }),
    checkedAt: textOf(data.checkedAt, 'checkedAt'),
  }
}

/** `receiptAttempt.canRetryNow` продажи (RP-FB35 K4); попытки не было или поля нет - `null`. */
function decodeCanRetryNow(raw: unknown, field: string): boolean | null {
  if (raw === null || raw === undefined) return null
  if (typeof raw !== 'object') return fail(`${field} — не объект`)
  return optionalBool((raw as Record<string, unknown>).canRetryNow, `${field}.canRetryNow`)
}
